import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Expand } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';

// El orden importa: los `span` estan calculados para que el mosaico no deje
// huecos ni en 4 columnas (desktop) ni en 2 (movil), y para que los primeros
// 6 formen por si solos un bloque completo (los usa el preview del home).
export const GALLERY_IMAGES = [
  { src: '/images/galeria/01.jpg', alt: 'Alberca y jacuzzi frente a la terraza techada', span: 'col-span-2 row-span-2' },
  { src: '/images/galeria/10.jpg', alt: 'Vista aérea de la villa, la alberca y los jardines', span: 'col-span-2' },
  { src: '/images/galeria/04.jpg', alt: 'Asador tipo argentino con parrilla de ladrillo', span: '' },
  { src: '/images/galeria/06.jpg', alt: 'Alberca de mosaico azul con jacuzzi integrado', span: '' },
  { src: '/images/galeria/02.jpg', alt: 'Andador del jardín con camastros y muro de botellas', span: 'col-span-2' },
  { src: '/images/galeria/12.jpg', alt: 'La alberca y el jacuzzi vistos desde la terraza', span: 'col-span-2' },
  { src: '/images/galeria/03.jpg', alt: 'La alberca y la palapa bajo el cielo despejado', span: 'col-span-2 row-span-2' },
  { src: '/images/galeria/11.jpg', alt: 'Vista aérea de la alberca y la terraza principal', span: 'col-span-2' },
  { src: '/images/galeria/05.jpg', alt: 'Terraza con comedor de madera y barra junto a la alberca', span: '' },
  { src: '/images/galeria/08.jpg', alt: 'Terraza techada con barra y comedor para eventos', span: '' },
  { src: '/images/galeria/13.jpg', alt: 'Vista aérea del conjunto y sus jardines', span: 'col-span-2' },
  { src: '/images/galeria/07.jpg', alt: 'Rincón de descanso con banca y muro de hiedra', span: '' },
  { src: '/images/galeria/09.jpg', alt: 'Fachada de la villa de dos niveles junto a la alberca', span: '' },
];

export default function Gallery({ images = GALLERY_IMAGES, limit }) {
  const shown = limit ? images.slice(0, limit) : images;
  const [openIndex, setOpenIndex] = useState(null);
  const isOpen = openIndex !== null;
  const current = isOpen ? shown[openIndex] : null;

  const close = useCallback(() => setOpenIndex(null), []);
  const next = useCallback(
    () => setOpenIndex((i) => (i === null ? i : (i + 1) % shown.length)),
    [shown.length]
  );
  const prev = useCallback(
    () => setOpenIndex((i) => (i === null ? i : (i - 1 + shown.length) % shown.length)),
    [shown.length]
  );

  // Navegacion con teclado (Escape ya lo maneja Radix)
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, next, prev]);

  return (
    <>
      <div
        data-testid="gallery-grid"
        className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 auto-rows-[130px] sm:auto-rows-[180px] lg:auto-rows-[210px]"
      >
        {shown.map((image, index) => (
          <motion.button
            key={image.src}
            type="button"
            onClick={() => setOpenIndex(index)}
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5, delay: (index % 4) * 0.07 }}
            className={`group relative overflow-hidden rounded-2xl bg-[#E0D8CC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8962D] focus-visible:ring-offset-2 ${image.span}`}
            aria-label={`Ampliar foto: ${image.alt}`}
          >
            <img
              src={image.src}
              alt={image.alt}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />

            {/* Velo sutil al pasar el cursor. Sin pie de foto: la descripcion
                vive solo en el alt, que no se pinta pero si lo leen los
                lectores de pantalla y los buscadores. */}
            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-300" />
            <span className="absolute top-3 right-3 grid h-9 w-9 place-items-center rounded-full bg-white/90 text-[#5C4033] opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 group-focus-visible:opacity-100 transition-all duration-300">
              <Expand className="h-4 w-4" />
            </span>
          </motion.button>
        ))}
      </div>

      {/* Lightbox */}
      <Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
        <DialogContent
          aria-describedby={undefined}
          className="max-w-none w-screen h-screen border-0 rounded-none sm:rounded-none bg-black/95 p-0 gap-0 [&>button]:hidden"
        >
          <DialogTitle className="sr-only">{current?.alt ?? 'Galería'}</DialogTitle>

          {current && (
            <div className="relative h-full w-full flex items-center justify-center">
              <img
                src={current.src}
                alt={current.alt}
                className="max-h-[82vh] max-w-[92vw] object-contain select-none"
              />

              <button
                type="button"
                onClick={close}
                aria-label="Cerrar galería"
                className="absolute top-4 right-4 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white hover:bg-white/25 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>

              {shown.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prev}
                    aria-label="Foto anterior"
                    className="absolute left-2 sm:left-6 grid h-11 w-11 sm:h-14 sm:w-14 place-items-center rounded-full bg-white/10 text-white hover:bg-white/25 transition-colors"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    type="button"
                    onClick={next}
                    aria-label="Foto siguiente"
                    className="absolute right-2 sm:right-6 grid h-11 w-11 sm:h-14 sm:w-14 place-items-center rounded-full bg-white/10 text-white hover:bg-white/25 transition-colors"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </>
              )}

              <p className="absolute inset-x-0 bottom-6 text-center text-white/50 text-xs">
                {openIndex + 1} / {shown.length}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
